import React, { useEffect, useState } from 'react';
import "../styles/MockReport.css";
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Sanitization — strips backend/internal identifiers out of the report text
// before it's ever parsed or rendered. This is defense-in-depth: the real
// fix belongs on the backend (report generation shouldn't put internal IDs
// into candidate-facing text at all), but the frontend should never trust
// that assumption blindly.
//
// Two layers:
//   1. Exact match on the current session id (strongest signal — we know
//      this value for certain).
//   2. Generic pattern matches for common machine-identifier shapes
//      (UUIDs, Mongo ObjectIds, JWTs, "prefix-digits-alnum" tokens) so
//      that IDs which *aren't* the current session id, or don't match it
//      exactly, still get caught instead of leaking straight through.
// ---------------------------------------------------------------------------
const GENERIC_ID_PATTERN = /\b[a-z][a-z0-9]{1,15}-\d{6,}-[a-z0-9]{3,}\b/gi; // any-prefix-digits-alnum
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const MONGO_OBJECTID_PATTERN = /\b[0-9a-f]{24}\b/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Strips any occurrence of the current session id (exact match) plus any
 * generically-shaped backend identifier from the raw markdown BEFORE
 * parsing, so nothing internal can end up in a rendered section, bullet,
 * or paragraph.
 */
function sanitizeMarkdown(markdown, sessionId) {
  if (!markdown) return markdown;
  let sanitized = markdown;

  const trimmedId = sessionId ? String(sessionId).trim() : '';
  if (trimmedId) {
    const escaped = escapeRegExp(trimmedId);
    sanitized = sanitized.replace(new RegExp(escaped, 'gi'), '[no response recorded]');
  }

  sanitized = sanitized
    .replace(JWT_PATTERN, '[no response recorded]')
    .replace(UUID_PATTERN, '[no response recorded]')
    .replace(MONGO_OBJECTID_PATTERN, '[no response recorded]')
    .replace(GENERIC_ID_PATTERN, '[no response recorded]');

  return sanitized;
}

function extractMarkdown(raw) {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const candidateKeys = ['report', 'data', 'markdown', 'content', 'message', 'result'];
    for (const key of candidateKeys) {
      if (typeof raw[key] === 'string') return raw[key];
    }
  }
  return '';
}

/** Classifies a section by its emoji / title so we can style it. */
function classifySection(title) {
  const t = title.toLowerCase();
  if (title.includes('✅') || t.includes('strength')) return 'strength';
  if (title.includes('❌') || t.includes('mistake')) return 'mistake';
  if (title.includes('📈') || t.includes('gap')) return 'gap';
  if (title.includes('🎯') || t.includes('rating')) return 'rating';
  return 'summary';
}

/** Strips leading emoji + markdown heading markers, returns clean label. */
function cleanTitle(title) {
  return title
    .replace(/^[^\w]*/u, '')
    .replace(/\*\*/g, '')
    .trim();
}

/** Parses a "- **Label**: rest of sentence" bullet into label + body. */
function parseBulletText(text) {
  const stripped = text.replace(/^-\s*/, '');
  const match = stripped.match(/^\*\*(.+?)\*\*:?\s*(.*)$/);
  if (match) {
    return { label: match[1], body: match[2] };
  }
  return { label: null, body: stripped };
}

/**
 * Turns the raw markdown report into a structured object for rendering.
 * Handles:
 *  - "# " or "## " section headings (the API is inconsistent about depth)
 *  - an optional leading "# Performance Report for X's Interview" title line
 *  - top-level bullets and indented sub-bullets (nested lists)
 */
function parseReport(markdown) {
  const rawLines = markdown.split('\n');
  const lines = rawLines.filter((l) => l.trim().length > 0);

  let name = null;
  let startIdx = 0;

  const firstTrimmed = lines[0] ? lines[0].trim() : '';
  // Accept both straight (') and curly (’) apostrophes from the API.
  const titleMatch = firstTrimmed.match(/^#{1,3}\s*Performance Report for\s+(.+?)['\u2019]s\s+Interview/i);
  if (titleMatch) {
    name = titleMatch[1];
    startIdx = 1;
  }

  const sections = [];
  let current = null;
  let lastTopBullet = null;

  for (let i = startIdx; i < lines.length; i++) {
    const raw = lines[i];
    const trimmed = raw.trim();
    const indent = raw.length - raw.trimStart().length;

    const headingMatch = trimmed.match(/^#{1,3}\s*(.+)$/);
    if (headingMatch && indent === 0) {
      if (current) sections.push(current);
      const rawTitle = headingMatch[1];
      current = {
        rawTitle,
        title: cleanTitle(rawTitle),
        type: classifySection(rawTitle),
        bullets: [],
        paragraphs: [],
      };
      lastTopBullet = null;
      continue;
    }

    if (!current) continue;

    if (trimmed.startsWith('- ')) {
      const parsed = parseBulletText(trimmed);
      if (indent >= 2 && lastTopBullet) {
        lastTopBullet.children.push(parsed);
      } else {
        const bullet = { ...parsed, children: [] };
        current.bullets.push(bullet);
        lastTopBullet = bullet;
      }
    } else {
      current.paragraphs.push(trimmed.replace(/\*\*/g, ''));
    }
  }
  if (current) sections.push(current);

  let rating = null;
  const ratingSection = sections.find((s) => s.type === 'rating');
  if (ratingSection) {
    const m = ratingSection.rawTitle.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+)/);
    if (m) rating = { score: parseFloat(m[1]), max: parseFloat(m[2]) };
  }

  return { name, sections, rating };
}

const GUTTER = {
  strength: '✓',
  mistake: '✕',
  gap: '!',
};

function BulletLine({ bullet, type }) {
  return (
    <div className="report__line">
      <span className="report__line-gutter">{GUTTER[type] || '•'}</span>
      <div className="report__line-text">
        {bullet.label && <strong>{bullet.label}: </strong>}
        {bullet.body}
        {bullet.children && bullet.children.length > 0 && (
          <div className="report__sublist">
            {bullet.children.map((child, i) => (
              <div className="report__subitem" key={i}>
                {child.label && <strong>{child.label}: </strong>}
                {child.body}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ section }) {
  if (section.type === 'rating') return null; // rendered separately as the verdict bar

  return (
    <div className={`report__section report__section--${section.type}`}>
      <div className="report__section-head">
        <h2 className="report__section-title">{section.title}</h2>
        <span className="report__section-count">
          {section.bullets.length > 0
            ? `${section.bullets.length} item${section.bullets.length === 1 ? '' : 's'}`
            : 'summary'}
        </span>
      </div>

      {section.paragraphs.length > 0 && (
        <p className="report__comment">{section.paragraphs.join(' ')}</p>
      )}

      {section.bullets.length > 0 && (
        <div className="report__lines">
          {section.bullets.map((b, i) => (
            <BulletLine bullet={b} type={section.type} key={i} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MockReport() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [report, setReport] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Fresh read on every mount — not cached at module load time
  const [mocksessionId] = useState(() => localStorage.getItem("mockInterviewSessionId"));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const token = localStorage.getItem("token");
        if (!mocksessionId) throw new Error('No interview session found');
        if (!token) throw new Error('You are not signed in');

        const API_URL =
          `https://codematrix.co.in/auth/api/users/api/mock-interview/report?sessionId=${mocksessionId}`;

        const res = await fetch(API_URL, {
          headers: { Authorization: `${token}` }
        });
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

        let raw;
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          try {
            raw = await res.json();
          } catch (parseErr) {
            // Header claimed JSON but body wasn't valid JSON — don't crash, fall back.
            raw = '';
          }
        } else {
          raw = await res.text();
        }

        const rawMarkdown = extractMarkdown(raw);
        if (!rawMarkdown) throw new Error('No report content in response');

        // Strip the session id (and any backend-identifier-shaped text)
        // before parsing, so it can never end up in a rendered section,
        // bullet, or paragraph.
        const markdown = sanitizeMarkdown(rawMarkdown, mocksessionId);

        const parsed = parseReport(markdown);
        if (!cancelled) {
          setReport(parsed);
          setStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err.message || 'Something went wrong');
          setStatus('error');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [mocksessionId]);

  if (status === 'loading') {
    return (
      <div className="report">
        <div className="report__status">
          <div className="report__spinner" />
          Fetching interview report…
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="report">
        <div className="report__status">
          <div className="report__status-code">Report unavailable</div>
          {errorMsg}
        </div>
      </div>
    );
  }

  const bodySections = report.sections.filter((s) => s.type !== 'rating');
  const ratingSection = report.sections.find((s) => s.type === 'rating');

  return (
    <div className="report">
      <div className="report__container">
        <div className="report__window">
          <div className="report__titlebar">
            <div className="report__titlebar-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
              Interview Performance Report
            </div>
          </div>

          <div className="report__body">
            <div className="report__heading-row">
              <div>
                <h1 className="report__name">{report.name || 'Interview Report'}</h1>
                <p className="report__subtitle">Performance evaluation summary</p>
              </div>
              {report.rating && (
                <div className="report__badge">
                  <span className="report__badge-icon" />
                  <span className="report__badge-text">
                    {report.rating.score}/{report.rating.max}
                    <span>Overall score</span>
                  </span>
                </div>
              )}
            </div>

            {bodySections.map((section, i) => (
              <Section section={section} key={i} />
            ))}

            {ratingSection && (
              <div className="report__verdict">
                <div className="report__verdict-score">
                  {report.rating ? report.rating.score : '—'}
                  <span> /{report.rating ? report.rating.max : 10}</span>
                </div>
                <div className="report__verdict-divider" />
                <p className="report__verdict-text">
                  {ratingSection.paragraphs.join(' ')}
                </p>
              </div>
            )}
          </div>
          <div className="report__actions">
            <button
              className="report__done-btn"
              onClick={() => navigate("/front")}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}